import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { defaultRange, num, rangeParams, usd, useLoad } from "@/lib/staff";
import { ErrorBox, Pager, RangePicker, Skeleton, Stat, Table } from "@/components/staff/ui";
import { TrackingNote, modelColumns } from "@/components/staff/bits";

const STUDENT_COLS = [
  { key: "email", label: "Student", render: (r) => (r.user_id === "system" ? <span className="text-neutral-500">Background work (no student)</span> : <span>{r.email || r.user_id}</span>) },
  { key: "requests", label: "Requests", right: true, render: (r) => num(r.requests) },
  { key: "failed", label: "Failed", right: true, render: (r) => num(r.failed) },
  { key: "tokens", label: "Tokens (est.)", right: true, render: (r) => num(r.tokens) },
  { key: "est_cost", label: "Estimated AI cost", right: true, render: (r) => usd(r.est_cost, 4) },
];

export function UsageTab() {
  const navigate = useNavigate();
  const [range, setRange] = useState(() => defaultRange("30d"));
  const [page, setPage] = useState(1);
  const params = useMemo(() => ({ ...rangeParams(range), page }), [range, page]);
  const { data: d, error, loading, reload } = useLoad("/staff/usage", params);
  return (
    <div className="space-y-4" data-testid="usage-tab">
      <RangePicker value={range} onChange={(r) => { setRange(r); setPage(1); }} testid="usage-range" />
      {error && <ErrorBox error={error} onRetry={reload} />}
      {!d && loading && <Skeleton rows={5} />}
      {d && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Stat label="AI requests" value={num(d.totals.requests)} testid="usage-requests" />
            <Stat label="Failed AI requests" value={num(d.totals.failed)} testid="usage-failed" />
            <Stat label="Tokens (estimated)" value={num(d.totals.tokens)} testid="usage-tokens" />
            <Stat label="Estimated AI cost" value={usd(d.totals.est_cost)} hint="Our estimate from the rates below, not a Stripe amount" testid="usage-est-cost" />
          </div>
          <Table testid="usage-models" columns={modelColumns(true)} rows={d.models} rowKey={(r) => r.model} empty="No AI requests in this range." maxH="max-h-80" />
          <h3 className="text-sm font-medium text-neutral-200 pt-2">By student</h3>
          <Table testid="usage-students" columns={STUDENT_COLS} rows={d.items} rowKey={(r) => r.user_id} empty="No AI requests in this range."
            onRowClick={(r) => r.user_id !== "system" && navigate(`/staff/students/${r.user_id}`)} />
          <Pager page={page} pageSize={d.page_size} total={d.total} onPage={setPage} testid="usage-pager" />
          <TrackingNote since={d.tracking_since} />
        </>
      )}
    </div>
  );
}
